import React, { useState, useEffect } from "react";
import { useL } from "@/lib/useL";
import { Link } from "react-router-dom";
import { BookOpen, X, ArrowRight } from "lucide-react";
import { KDP_AVAILABLE } from "@/lib/kdpConfig";

/**
 * Small, dismissable banner promoting the KDP book after a summary
 * is generated. Only shown to teachers-friendly users (heuristic: any
 * authenticated user gets it). Dismissed state persists in
 * localStorage so we never nag returning readers.
 *
 * Rules:
 * - Appears max ONCE per browser (dismissed forever after 1st close)
 * - Hidden entirely if user visited /enseignants in the past
 * - Never blocks the summary itself — sits UNDER it, above the fold
 *   of secondary actions
 */
export const BookPromoBanner = () => {
  const L = useL();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    // Read from localStorage on mount
    const wasDismissed =
      localStorage.getItem("sux1_book_banner_dismissed") === "1";
    const visitedEnseignants =
      localStorage.getItem("sux1_visited_enseignants") === "1";
    setDismissed(wasDismissed || visitedEnseignants);
  }, []);

  const handleDismiss = () => {
    localStorage.setItem("sux1_book_banner_dismissed", "1");
    setDismissed(true);
  };

  const handleClick = () => {
    // Mark as visited so we don't re-show on next summary
    localStorage.setItem("sux1_visited_enseignants", "1");
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="book-promo-banner"
      className="mt-8 max-w-3xl border-l-4 border-[#002FA7] bg-[#002FA7]/[0.08] dark:bg-[#0a1430] p-4 flex items-start gap-4 relative animate-in fade-in slide-in-from-bottom-2 duration-500"
    >
      <div className="flex-shrink-0 w-12 h-16 bg-[#002FA7] flex items-center justify-center">
        <BookOpen className="w-6 h-6 text-white" />
      </div>

      <div className="flex-1 min-w-0 pr-6">
        <p className="font-mono uppercase tracking-[0.15em] text-[9px] text-[#002FA7] mb-1">
          {L("Nouveau livre · Édition 2026", "New book · 2026 edition (in French)")}
        </p>
        <h3 className="font-serif text-base font-bold text-foreground leading-tight mb-1">
          {L("L'IA au service des enseignants", "AI for teachers")}
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed mb-2">
          {L("50 cas pratiques pour gagner 2 heures par jour. Écrit pour les profs pressés, pas pour les philosophes de la tech.", "50 practical cases to save 2 hours a day. Written for busy teachers, not tech philosophers.")}
        </p>
        <Link
          to="/enseignants"
          onClick={handleClick}
          data-testid="book-promo-banner-cta"
          className="inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7] hover:text-[#002FA7] transition-colors"
        >
          {KDP_AVAILABLE ? L("Découvrir le livre", "Discover the book") : L("Voir la sortie", "See the release")}
          <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <button
        type="button"
        onClick={handleDismiss}
        data-testid="book-promo-banner-dismiss"
        aria-label="Fermer"
        className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
